/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero — Cálculo de totales, impuestos y cambio (LÓGICA PURA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: este archivo es `.ts` PURO. NO importa macros de lingui (`msg`/`t`) ni
 * nada de UI. Recibe números (centavos / basis points) y devuelve números.
 * Los strings traducidos los aporta el `.tsx` que consume estos helpers.
 *
 * Convenciones:
 *   - Todos los montos son enteros en unidades menores (centavos).
 *   - `tax_rate` se expresa en basis points: 1600 = 16%.
 *   - El IVA aquí se trata como AÑADIDO (no incluido) para el MVP.
 */

const BASIS_POINTS = 10_000;

export interface CalcModifier {
  /** Delta de precio en centavos (puede ser negativo). */
  priceDelta: number;
}

export interface CalcLine {
  /** Precio unitario base del producto, en centavos. */
  unitPrice: number;
  /** Cantidad (entero ≥ 1). */
  qty: number;
  /** Tasa de impuesto en basis points (1600 = 16%). */
  taxRate?: number;
  /** Modificadores aplicados a la línea. */
  modifiers?: CalcModifier[];
}

export interface OrderTotals {
  /** Suma de líneas (base + modificadores) × qty, antes de impuestos y descuento. */
  subtotal: number;
  /** Descuento aplicado a nivel ticket, en centavos. */
  discount: number;
  /** Impuesto total, en centavos. */
  taxTotal: number;
  /** Total a cobrar = subtotal − descuento + impuesto. */
  total: number;
}

/** Precio efectivo de una unidad de la línea (base + suma de modificadores). */
export function lineUnitEffectivePrice(line: CalcLine): number {
  const mods = line.modifiers ?? [];
  const modSum = mods.reduce((acc, m) => acc + m.priceDelta, 0);
  return line.unitPrice + modSum;
}

/** Total de una línea (precio efectivo × cantidad), antes de impuestos. */
export function lineTotal(line: CalcLine): number {
  const qty = Math.max(0, Math.trunc(line.qty));
  return lineUnitEffectivePrice(line) * qty;
}

/**
 * Calcula los totales de una orden a partir de sus líneas.
 *
 * El descuento de ticket se reparte proporcionalmente sobre la base imponible
 * para que el impuesto se calcule sobre el neto ya descontado (modelo común en
 * POS de LatAm). El impuesto se redondea por línea para evitar descuadres de
 * 1 centavo en el arqueo.
 */
export function computeOrderTotals(lines: CalcLine[], discount = 0): OrderTotals {
  const subtotal = lines.reduce((acc, l) => acc + lineTotal(l), 0);
  const safeDiscount = clampDiscount(discount, subtotal);

  // Factor de descuento proporcional aplicado a cada línea.
  const netFactor = subtotal > 0 ? (subtotal - safeDiscount) / subtotal : 0;

  let taxTotal = 0;
  for (const line of lines) {
    const rate = line.taxRate ?? 0;
    if (rate <= 0) {
      continue;
    }
    const lineNet = lineTotal(line) * netFactor;
    taxTotal += Math.round((lineNet * rate) / BASIS_POINTS);
  }

  const total = subtotal - safeDiscount + taxTotal;
  return { subtotal, discount: safeDiscount, taxTotal, total };
}

/** El descuento nunca puede ser negativo ni superar el subtotal. */
export function clampDiscount(discount: number, subtotal: number): number {
  const d = Math.max(0, Math.trunc(discount));
  return Math.min(d, Math.max(0, subtotal));
}

/**
 * Cambio a devolver en un pago en efectivo.
 * `tendered` = efectivo entregado por el cliente; `total` = monto a cobrar.
 * Devuelve 0 si el cliente entregó justo o de menos (no hay cambio negativo).
 */
export function changeDue(tendered: number, total: number): number {
  return Math.max(0, Math.trunc(tendered) - Math.trunc(total));
}

/** Suma de pagos registrados, en centavos. */
export function sumPayments(amounts: number[]): number {
  return amounts.reduce((acc, a) => acc + Math.trunc(a), 0);
}

/** Saldo pendiente de una orden (total − pagado). 0 si ya está saldada. */
export function remainingDue(total: number, paidAmounts: number[]): number {
  return Math.max(0, Math.trunc(total) - sumPayments(paidAmounts));
}

/** Suma de propinas registradas, en centavos. */
export function sumTips(tips: number[]): number {
  return tips.reduce((acc, t) => acc + Math.max(0, Math.trunc(t)), 0);
}

/**
 * Reparto equitativo de un total entre `parts` comensales, en centavos.
 * El último recibe el resto para que la suma cuadre EXACTA con el total
 * (sin perder ni inventar centavos por redondeo).
 */
export function splitEvenly(total: number, parts: number): number[] {
  const n = Math.max(1, Math.trunc(parts));
  const t = Math.max(0, Math.trunc(total));
  const base = Math.floor(t / n);
  const out = Array.from({ length: n }, () => base);
  out[n - 1] = (out[n - 1] ?? base) + (t - base * n);
  return out;
}

/**
 * Importe de un pago al dividir por ítems: suma de los `line_total` asignados a
 * ese comensal/pago. Recibe ya los totales de las líneas seleccionadas.
 */
export function splitByItems(lineTotals: number[]): number {
  return lineTotals.reduce((acc, v) => acc + Math.max(0, Math.trunc(v)), 0);
}

/**
 * ¿La suma de los importes de pago (sin propina) cubre exactamente el total?
 * El split es válido cuando paga el total justo (no sobra ni falta). Las
 * propinas son adicionales y NO cuentan para cubrir el total.
 */
export function isSplitSettled(total: number, paymentAmounts: number[]): boolean {
  return sumPayments(paymentAmounts) >= Math.trunc(total);
}

export interface CashboxReconciliation {
  /** Efectivo esperado = fondo inicial + ventas en efectivo − salidas. */
  expected: number;
  /** Efectivo contado al cierre. */
  counted: number;
  /** Descuadre (counted − expected): positivo sobra, negativo falta. */
  variance: number;
}

/**
 * Arqueo de caja: cuadra el efectivo contado contra el esperado.
 *
 * `cashMovements` son los movimientos de la sesión con su signo ya resuelto en
 * `signedAmount` (entradas/ventas positivas, salidas/retiros negativos).
 */
export function reconcileCashbox(
  openingFloat: number,
  signedMovements: number[],
  countedTotal: number,
): CashboxReconciliation {
  const expected = Math.trunc(openingFloat) + sumPayments(signedMovements);
  const counted = Math.trunc(countedTotal);
  return { expected, counted, variance: counted - expected };
}
